import React, { useCallback, useState } from "react";

async function askHelp(message, accessToken) {
  const res = await fetch("/api/help/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ access_token: accessToken, message }),
  });
  return res.json();
}

const HelpChatPanel = ({ accessToken, handoffUrl, phoneNumber, title = "Support Chat" }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [needsHandoff, setNeedsHandoff] = useState(false);

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || isSending) {
      return;
    }
    if (!accessToken) {
      setError("Please sign in to contact support.");
      return;
    }

    const nextMessages = [...messages, { role: "user", content: trimmed }];
    setMessages(nextMessages);
    setInput("");
    setIsSending(true);
    setError("");

    try {
      const data = await askHelp(trimmed, accessToken);
      if (data?.error) {
        setIsSending(false);
        setError(data.error);
        return;
      }
      if (data?.answer) {
        setMessages([...nextMessages, { role: "assistant", content: data.answer }]);
        setNeedsHandoff(Boolean(data.needsHandoff));
        setIsSending(false);
        setError("");
        return;
      }
      setIsSending(false);
      setError("Unexpected response from support. Please try again.");
    } catch (err) {
      setIsSending(false);
      setError(err?.message || "Unable to reach support. Please try again.");
    }
  }, [accessToken, input, isSending, messages]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const openHandoff = useCallback(() => {
    if (!handoffUrl) {
      return;
    }
    window.open(handoffUrl, "_blank", "noopener,noreferrer");
  }, [handoffUrl]);

  const renderMessage = useCallback((msg, index) => {
    const isUser = msg.role === "user";
    const bubbleStyle = isUser ? userBubble : assistantBubble;
    const rowStyle = isUser ? rowRight : rowLeft;

    return (
      <div key={`${msg.role}-${index}`} style={rowStyle}>
        <div style={bubbleStyle}>{msg.content}</div>
      </div>
    );
  }, []);

  return (
    <div style={panelCard} className="help-chat-panel">
      <div style={panelHeader}>
        <strong>{title}</strong>
      </div>
      <div style={panelBody}>
        {messages.length === 0 && (
          <div style={emptyState}>
            Ask a question and we will help you out. You can also call us at {phoneNumber || "our support line"}.
          </div>
        )}
        <div style={messagesList}>
          {messages.map(renderMessage)}
        </div>
        {error && <div style={errorText}>{error}</div>}
        {needsHandoff && (
          <div style={handoffRow}>
            <span>Would you like to contact support directly?</span>
            <button type="button" onClick={openHandoff} style={handoffBtn}>
              Contact support
            </button>
          </div>
        )}
      </div>
      <div style={panelFooter}>
        <textarea
          rows={2}
          placeholder="Type your message..."
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          style={inputBox}
        />
        <button type="button" onClick={handleSend} style={sendBtn} disabled={isSending}>
          {isSending ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  );
};

const panelCard = {
  width: "100%",
  maxWidth: 560,
  maxHeight: "80vh",
  background: "var(--helpchat-bg, #fff)",
  borderRadius: 12,
  border: "1px solid var(--helpchat-border, #eef2f7)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
  overflow: "hidden",
  fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif",
  display: "flex",
  flexDirection: "column",
  color: "var(--helpchat-text, #111827)",
};

const panelHeader = {
  padding: "12px 14px",
  borderBottom: "1px solid var(--helpchat-border, #eee)",
};

const panelBody = {
  padding: "12px 14px",
  minHeight: 180,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const messagesList = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 8,
  maxHeight: "40vh",
  overflowY: "auto",
};

const panelFooter = {
  padding: "12px 14px 16px",
  borderTop: "1px solid var(--helpchat-border, #eee)",
  display: "flex",
  gap: 8,
  alignItems: "flex-end",
};

const inputBox = {
  flex: 1,
  resize: "none",
  borderRadius: 8,
  border: "1px solid var(--helpchat-input-border, #d1d5db)",
  padding: "8px 10px",
  fontSize: 14,
  background: "var(--helpchat-input-bg, #fff)",
  color: "var(--helpchat-text, #111827)",
};

const sendBtn = {
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid var(--helpchat-primary, #4C6EF8)",
  background: "var(--helpchat-primary, #4C6EF8)",
  color: "var(--helpchat-primary-text, #fff)",
  cursor: "pointer",
  fontSize: 14,
};

const rowLeft = {
  display: "flex",
  justifyContent: "flex-start",
};

const rowRight = {
  display: "flex",
  justifyContent: "flex-end",
};

const assistantBubble = {
  background: "var(--helpchat-assistant-bg, #f3f4f6)",
  color: "var(--helpchat-assistant-text, #111827)",
  borderRadius: 12,
  padding: "8px 12px",
  maxWidth: "75%",
  fontSize: 14,
  lineHeight: 1.4,
};

const userBubble = {
  background: "var(--helpchat-user-bg, #2563eb)",
  color: "var(--helpchat-user-text, #fff)",
  borderRadius: 12,
  padding: "8px 12px",
  maxWidth: "75%",
  fontSize: 14,
  lineHeight: 1.4,
};

const emptyState = {
  fontSize: 14,
  color: "var(--helpchat-muted, #6b7280)",
};

const errorText = {
  marginTop: 8,
  color: "var(--helpchat-error, #b91c1c)",
  fontSize: 13,
};

const handoffRow = {
  marginTop: 12,
  display: "flex",
  flexWrap: "wrap",
  gap: 8,
  alignItems: "center",
  fontSize: 13,
};

const handoffBtn = {
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--helpchat-primary, #4C6EF8)",
  background: "var(--helpchat-bg, #fff)",
  color: "var(--helpchat-primary, #4C6EF8)",
  cursor: "pointer",
};

export default HelpChatPanel;
